<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed, ref } from 'vue';

  import type { Invoice, Partner, Task } from '@stuut/api/models';

  import { stuut } from '@/api';
  import InvoiceDrawer from '@/components/common/InvoiceDrawer.vue';
  import TaskPromisedPayment from '@/components/tasks/TaskPromisedPayments/TaskPromisedPayment.vue';

  interface Props {
    partner: Partner;
    task: Task;
  }

  const props = defineProps<Props>();
  const selectedInvoice = ref<Invoice | null>(null);
  const showInvoiceSheet = ref(false);
  const fetchingInvoiceId = ref<string | null>(null);

  const taskPromise = computed(() => {
    // Handle the array type properly
    const data = props.task?.data;
    if (Array.isArray(data)) {
      // Find promise data in the array
      const promiseData = data.find((item: any) => item.promise);
      return (promiseData as any)?.promise || null;
    }
    return (data as any)?.promise || null;
  });

  const peekFindInvoice = async (invoiceId: string) => {
    const record = props.task?.invoices?.find((inv) => inv.id === invoiceId);

    if (record) return record;

    try {
      fetchingInvoiceId.value = invoiceId;

      const invoice = await stuut.invoices.retrieve(invoiceId);
      return invoice || null;
    } finally {
      fetchingInvoiceId.value = null;
    }
  };

  const handleSheetClose = () => {
    selectedInvoice.value = null;
    showInvoiceSheet.value = false;
  };

  const handleSheetOpen = async (invoiceId: string) => {
    selectedInvoice.value = await peekFindInvoice(invoiceId);

    showInvoiceSheet.value = true;
  };
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center gap-x-1.5">
      <i class="far fa-calendar-day text-2xs!"></i>

      <strong> {{ pluralize('Promised Date', task?.invoices?.length) }}: </strong>
    </div>

    <div class="w-full border rounded">
      <div
        class="grid grid-cols-[minmax(135px,auto)_82px_70px_125px_70px_46px_70px_82px_1fr] items-center gap-x-1 w-full h-7 text-muted-foreground font-semibold px-4"
      >
        <div>Invoice</div>
        <div>Due date</div>
        <div>Balance</div>
        <div>
          Promised date
          <span v-if="taskPromise?.is_promised_date_updated" class="text-primary font-bold">
            NEW
          </span>
        </div>
        <div>Amount</div>
        <div>Type</div>
        <div>Source</div>
        <div>Status</div>
      </div>

      <template v-for="invoiceId in (task?.contextData?.data as any)?.invoice_ids" :key="invoiceId">
        <div
          class="grid grid-cols-[minmax(135px,auto)_82px_70px_125px_70px_46px_70px_82px_1fr] items-center gap-x-1 w-full h-10 border-t px-4"
        >
          <TaskPromisedPayment
            :promise-data="{
              invoice_id: invoiceId,
              ...task.invoices?.find((i) => i.id === invoiceId),
              invoice_number: task.invoices?.find((i) => i.id === invoiceId)?.number,
            }"
            :task
            :partner
            :fetching-invoice-id="fetchingInvoiceId"
            @open-sheet="handleSheetOpen"
            @close-sheet="handleSheetClose"
          />
        </div>
      </template>
    </div>

    <InvoiceDrawer
      v-if="selectedInvoice && props.partner"
      v-model:open="showInvoiceSheet"
      :invoice="selectedInvoice"
      :partner="props.partner"
      @close="handleSheetClose"
    />
  </div>
</template>

<style scoped></style>
